import { z } from 'zod';
import { DomainError } from './errors.js';
import type { Action } from './permissions.js';

/**
 * Personel ofis notları (ADR 0159; sahip 2026-11-04 B4): ofisin kişi hakkında yazdığı
 * değerlendirme, geri bildirim ve not. Performans ve davranış takibi içindir; kişiye ve gemiye
 * gösterilmez. Not silinmez, üzerine yazılmaz: düzeltme ve gerekçeli geri çekme notun yeni
 * sürümüdür (değiştirilemez `crew_note_versions`) [KVKK m.4 (2)(c) doğruluk, m.12 güvenlik].
 * Kişi KVKK m.11 ile isterse bütün notları ona verilir (ofiste dışa aktarma).
 *
 * Emsal: AMOS Crewing ve Adonis "appraisal / remarks" kayıtları ofise özeldir, kişiye açılmaz;
 * MLC A2.1 "record of employment" iş kalitesi değerlendirmesi içermez (ayrı tutulur).
 */

export const CREW_NOTE_KINDS = ['evaluation', 'feedback', 'note'] as const;
export type CrewNoteKind = (typeof CREW_NOTE_KINDS)[number];

/** Sürümün değişikliği: ilk yazım, düzeltme ya da gerekçeli geri çekme. */
export const CREW_NOTE_CHANGES = ['create', 'correct', 'withdraw'] as const;
export type CrewNoteChange = (typeof CREW_NOTE_CHANGES)[number];

export const CREW_NOTE_BODY_MAX = 4000;
export const CREW_NOTE_REASON_MAX = 500;

const paragraph = (value: string) =>
  value
    .normalize('NFC')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim().replace(/[ \t]+/g, ' '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
const line = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const optionalLine = z
  .string()
  .max(CREW_NOTE_REASON_MAX * 2)
  .nullish()
  .transform((value) => (value === null || value === undefined ? null : line(value) || null))
  .refine((value) => value === null || value.length <= CREW_NOTE_REASON_MAX);
const optionalId = z
  .uuid()
  .nullish()
  .transform((value) => value ?? null);

/** Notun ortak şeması (gövde, sunucu ve arayüz): tür, metin, isteğe bağlı gemi ve atama. */
export const crewNoteSchema = z.object({
  kind: z.enum(CREW_NOTE_KINDS),
  body: z
    .string()
    .max(CREW_NOTE_BODY_MAX * 2)
    .transform(paragraph)
    .refine((value) => value.length > 0 && value.length <= CREW_NOTE_BODY_MAX),
  vesselId: optionalId,
  assignmentId: optionalId,
  /** Düzeltmenin isteğe bağlı açıklaması. */
  reason: optionalLine,
});
export type CrewNoteInput = z.input<typeof crewNoteSchema>;
export type CrewNoteFields = z.output<typeof crewNoteSchema>;

export const crewNoteWithdrawalSchema = z.object({
  reason: z
    .string()
    .max(CREW_NOTE_REASON_MAX * 2)
    .transform(line)
    .refine((value) => value.length > 0 && value.length <= CREW_NOTE_REASON_MAX),
});
export type CrewNoteWithdrawalInput = z.input<typeof crewNoteWithdrawalSchema>;

function parsed<T>(result: z.ZodSafeParseResult<T>): T {
  if (result.success) return result.data;
  const field = String(result.error.issues[0]?.path[0] ?? '');
  throw new DomainError('VALIDATION_FAILED', field, field);
}

/** Notu doğrular ve biçimler; hata `VALIDATION_FAILED`, ayrıntı alan adı. */
export function parseCrewNote(input: unknown): CrewNoteFields {
  return parsed(crewNoteSchema.safeParse(input));
}

/** Geri çekme gerekçesi zorunludur (NR496 4.5.3f kalıbı: gerekçeli iptal). */
export function parseCrewNoteWithdrawal(input: unknown): { reason: string } {
  return parsed(crewNoteWithdrawalSchema.safeParse(input));
}

/** Notun güncel hâli: en büyük sürüm numaralı sürüm. */
export function currentCrewNote<T extends { versionNo: number }>(
  versions: readonly T[],
): T | undefined {
  return versions.reduce<T | undefined>(
    (best, version) => (best === undefined || version.versionNo > best.versionNo ? version : best),
    undefined,
  );
}

/** Sürüm değişikliğinin yetki matrisindeki işlemi (`crew_notes` modülü). */
export function crewNoteChangeAction(change: CrewNoteChange): Action {
  return change === 'create' ? 'create' : change === 'correct' ? 'edit' : 'void';
}
